import { t, Trans } from '@lingui/macro'
import { useWeb3React } from '@web3-react/core'
import { RowBetween } from 'components/Row'
import useENS from 'hooks/useENS'
import { useEffect, useState } from 'react'
import { AlertCircle, Check, ExternalLink as ExternalLinkIcon, X } from 'react-feather'
import { BAD_RECIPIENT_ADDRESSES } from 'state/swap/hooks-accountAPIbefore'
import styled from 'styled-components/macro'
import { ThemedText } from 'theme'
import { opacify } from 'theme/utils'
import { isAddress } from 'utils'
import { ExplorerDataType, getExplorerLink } from 'utils/getExplorerLink'

const ReceiverContainer = styled.div`
  background-color: ${({ theme }) => theme.backgroundModule};
  border-radius: 6px;
  padding: 14px;
`

const ReceiverHeader = styled(RowBetween)`
  margin-bottom: 12px;
`

const HeaderCloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  display: flex;
  align-items: center;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }

  > * {
    stroke: ${({ theme }) => theme.textSecondary};
  }
`

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.backgroundSurface};
  border-radius: 20px;
  padding: 10px 16px;
  gap: 6px;
`

const Input = styled.input`
  flex: 1;
  background: none;
  border: none;
  outline: none;
  font-size: 14px;
  color: ${({ theme }) => theme.textPrimary};
  font-weight: 500;

  ::placeholder {
    color: ${({ theme }) => theme.textTertiary};
  }
`

const ConfirmButton = styled.button<{ $show: boolean }>`
  background: ${({ theme }) => theme.accentAction};
  border: none;
  padding: 0;
  cursor: pointer;
  display: ${({ $show }) => ($show ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  transition: opacity 0.2s ease;
  width: 26px;
  height: 26px;
  border-radius: 50%;

  &:hover {
    opacity: 1;
  }

  > * {
    stroke: ${({ theme }) => theme.backgroundModule};
  }
`

const VerifiedBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${({ theme }) => theme.accentAction}22;
  border: 1px solid ${({ theme }) => theme.accentAction};
  color: ${({ theme }) => theme.accentAction};
`

const ClearButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  opacity: 0.9;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }

  > * {
    stroke: ${({ theme }) => theme.textSecondary};
  }
`

const ExternalLinkButton = styled.a`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  opacity: 0.9;
  transition: opacity 0.2s ease;
  text-decoration: none;

  &:hover {
    opacity: 1;
  }

  > * {
    stroke: ${({ theme }) => theme.textSecondary};
  }
`

const WarningContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.accentCritical};
  background-color: ${({ theme }) => opacify(10, theme.accentCritical)};
`

const WarningIcon = styled(AlertCircle)`
  width: 14px;
  height: 14px;
  min-width: 14px;
  color: ${({ theme }) => theme.accentCritical};
  margin-top: 2px;
`

const ENSResolvedAddress = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background-color: ${({ theme }) => theme.backgroundSurface};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const ResolvedAddressText = styled(ThemedText.Caption)`
  font-family: monospace;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
  word-break: break-all;
`

interface ReceiverSectionProps {
  recipient: string
  onChangeRecipient: (value: string) => void
  onShowWarning?: (pendingRecipient: string) => void
  onClose?: () => void
  onRefetchQuote?: () => void
}

export default function ReceiverSection({
  recipient,
  onChangeRecipient,
  onShowWarning,
  onClose,
  onRefetchQuote,
}: ReceiverSectionProps) {
  const { chainId, account } = useWeb3React()

  // Local input value (not confirmed yet)
  const [inputValue, setInputValue] = useState(recipient || '')
  const [pendingInput, setPendingInput] = useState(false)

  const lookupValue = (pendingInput ? inputValue : recipient)?.trim()
  const shouldUseENS = Boolean(
    lookupValue &&
      lookupValue.length > 2 &&
      lookupValue.includes('.') &&
      !lookupValue.endsWith('.') &&
      !isAddress(lookupValue)
  )
  const { address, loading, name } = useENS(shouldUseENS ? lookupValue : undefined)

  // Set connected account as default when opening
  useEffect(() => {
    if (!recipient && account) {
      setInputValue(account)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only on mount

  // Sync inputValue with recipient when recipient changes externally
  useEffect(() => {
    if (recipient !== inputValue && !pendingInput) {
      setInputValue(recipient || '')
    }
  }, [recipient, inputValue, pendingInput])

  const formattedAddress = isAddress(recipient)
  const error = Boolean(recipient.length > 0 && !loading && !address && !formattedAddress)
  const isBadRecipient = formattedAddress && BAD_RECIPIENT_ADDRESSES[formattedAddress]
  const isDifferentFromAccount = formattedAddress && account && formattedAddress.toLowerCase() !== account.toLowerCase()
  const hasCustomRecipient = recipient.length > 0 && (!account || recipient.toLowerCase() !== account.toLowerCase())

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value
    const withoutSpaces = input.replace(/\s+/g, '')
    setInputValue(withoutSpaces)
    setPendingInput(withoutSpaces !== recipient)
  }

  const handleConfirm = () => {
    const withoutSpaces = inputValue.replace(/\s+/g, '')
    const previousRecipient = recipient ?? ''
    const accountLower = account?.toLowerCase()
    const directAddress = isAddress(withoutSpaces)
    const ensResolvedAddress = address ? isAddress(address) || undefined : undefined
    const resolvedAddress = directAddress || ensResolvedAddress
    const resolvedLower = resolvedAddress?.toLowerCase()
    const isValidAddress = Boolean(resolvedAddress)
    const isDifferent = resolvedLower && accountLower ? resolvedLower !== accountLower : Boolean(resolvedLower)

    if (!isValidAddress) {
      if (!address || loading) {
        return
      }
    }

    if (isDifferent && onShowWarning && resolvedAddress) {
      onShowWarning(resolvedAddress)
      setPendingInput(false)
      return
    }

    const finalRecipient =
      resolvedLower && accountLower && resolvedLower === accountLower ? account ?? '' : withoutSpaces
    const hasChanged = finalRecipient !== previousRecipient
    onChangeRecipient(finalRecipient)
    setInputValue(finalRecipient)
    setPendingInput(false)
    if (hasChanged && resolvedAddress) {
      onRefetchQuote?.()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && pendingInput) {
      handleConfirm()
    }
  }

  const handleClear = () => {
    const previousWasCustom = hasCustomRecipient
    if (account) {
      onChangeRecipient(account)
      setInputValue(account)
    } else {
      onChangeRecipient('')
      setInputValue('')
    }
    setPendingInput(false)
    if (previousWasCustom) {
      onRefetchQuote?.()
    }
  }

  const addressForLink = name || address || formattedAddress || ''
  const explorerLink =
    chainId && addressForLink ? getExplorerLink(chainId, addressForLink, ExplorerDataType.ADDRESS) : undefined

  return (
    <ReceiverContainer>
      <ReceiverHeader>
        <ThemedText.SubHeaderSmall color="textSecondary">
          <Trans>
            Send to another wallet: <i>(optional)</i>
          </Trans>
        </ThemedText.SubHeaderSmall>
        <HeaderCloseButton onClick={() => onClose?.()} aria-label="Hide receiver section">
          <X size={16} />
        </HeaderCloseButton>
      </ReceiverHeader>

      <InputContainer>
        <Input
          type="text"
          placeholder={t`Wallet Address or ENS name (press Enter to confirm)`}
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />
        <ConfirmButton
          $show={pendingInput && inputValue.length > 0 && !loading}
          onClick={handleConfirm}
          aria-label="Confirm address"
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </ConfirmButton>
        {recipient && recipient !== account && !pendingInput && (
          <ClearButton onClick={handleClear} aria-label="Reset to connected wallet">
            <X size={15} />
          </ClearButton>
        )}
        {!pendingInput && !error && (formattedAddress || address) && (
          <VerifiedBadge>
            <Check size={14} />
          </VerifiedBadge>
        )}
        {explorerLink && !error && !pendingInput && (
          <ExternalLinkButton href={explorerLink} target="_blank" rel="noopener noreferrer">
            <ExternalLinkIcon size={14} />
          </ExternalLinkButton>
        )}
      </InputContainer>

      {name && address && (
        <ENSResolvedAddress>
          <ThemedText.Caption color="textTertiary" fontSize="11px">
            <Trans>Resolved to:</Trans>
          </ThemedText.Caption>
          <ResolvedAddressText>{address}</ResolvedAddressText>
        </ENSResolvedAddress>
      )}

      {error && (
        <WarningContainer>
          <WarningIcon />
          <ThemedText.Caption color="accentCritical">
            <Trans>Invalid address or ENS name</Trans>
          </ThemedText.Caption>
        </WarningContainer>
      )}

      {isBadRecipient && !error && (
        <WarningContainer>
          <WarningIcon />
          <ThemedText.Caption color="accentCritical">
            <Trans>Known contract address. Sending tokens here can result in loss of funds.</Trans>
          </ThemedText.Caption>
        </WarningContainer>
      )}

      {isDifferentFromAccount && !error && !isBadRecipient && (
        <WarningContainer>
          <WarningIcon />
          <ThemedText.Caption color="textSecondary" fontSize="12px">
            <Trans>Tokens will be sent to a different address.</Trans>
          </ThemedText.Caption>
        </WarningContainer>
      )}
    </ReceiverContainer>
  )
}
